import { formatDateToDDMMYYYY } from "../../utils/dateUtils";
import React, { useEffect, useState, useRef } from 'react';
import axios from '../../config/axiosConfig';
import { FaEdit, FaTrash, FaQrcode, FaFileCsv, FaUpload, FaFileDownload, FaSort, FaSortUp, FaSortDown, FaPhone, FaWhatsapp, FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';

const MembersList = ({ onAddClick, onEditClick, onMemberClick, filterType, preSelectedWing, preSelectedRole, preSelectedGender, preSelectedStatus, preSelectedState, preSelectedDistrict }) => {
    const [members, setMembers] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fontSize, setFontSize] = useState(18);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const tableTopRef = useRef(null);

    // Filters State
    const [searchQuery, setSearchQuery] = useState('');
    const [filterState, setFilterState] = useState(preSelectedState || '');
    const [filterDistrict, setFilterDistrict] = useState(preSelectedDistrict || '');
    const [filterRole, setFilterRole] = useState(preSelectedRole || '');
    const [filterStatus, setFilterStatus] = useState(preSelectedStatus || '');
    const [filterGender, setFilterGender] = useState(preSelectedGender || '');
    const [filterMemberType, setFilterMemberType] = useState('');
    const [filterWing, setFilterWing] = useState(preSelectedWing || '');
    const [wingSuggestions, setWingSuggestions] = useState([]);
    const [showWingSuggestions, setShowWingSuggestions] = useState(false);
    const [filterYear, setFilterYear] = useState('');
    const [filterMonth, setFilterMonth] = useState('');

    // Sorting State
    const [sortConfig, setSortConfig] = useState({ 
        key: preSelectedWing ? 'member_type_choice' : null, 
        direction: 'asc' 
    });

    // Hierarchical weights for leadership_place
    const roleWeightsList = [
        { key: "national regional", weight: 2 },
        { key: "national", weight: 1 },
        { key: "state regional - north", weight: 4 },
        { key: "state regional - south", weight: 5 },
        { key: "state regional - west", weight: 6 },
        { key: "state regional - east", weight: 7 },
        { key: "state", weight: 3 },
        { key: "district", weight: 8 },
        { key: "sub-district", weight: 9 },
        { key: "sub - district", weight: 9 },
        { key: "zone", weight: 10 },
        { key: "ward", weight: 11 }
    ];

    const getRoleWeight = (leadershipPlace) => {
        if (!leadershipPlace) return 99; // Default low priority
        const lowerPlace = leadershipPlace.toLowerCase();
        for (const role of roleWeightsList) {
            if (lowerPlace.includes(role.key)) return role.weight;
        }
        return 99;
    };

    // Dynamically calculate unique states from the members list
    const actualStates = React.useMemo(() => {
        const states = new Set(members.map(m => (m.state || '').trim().toUpperCase()).filter(s => s));
        return Array.from(states).sort();
    }, [members]);

    const availableYears = React.useMemo(() => {
        const years = new Set(members.map(m => m.registered_on ? new Date(m.registered_on).getFullYear() : null).filter(y => y));
        return Array.from(years).sort((a, b) => b - a);
    }, [members]);

    const months = [
        { value: '01', label: 'January' }, { value: '02', label: 'February' },
        { value: '03', label: 'March' }, { value: '04', label: 'April' },
        { value: '05', label: 'May' }, { value: '06', label: 'June' },
        { value: '07', label: 'July' }, { value: '08', label: 'August' },
        { value: '09', label: 'September' }, { value: '10', label: 'October' },
        { value: '11', label: 'November' }, { value: '12', label: 'December' }
    ];

    const memberRoleChoices = [
        "Citizen Reporter & Leader", 
        "Journalist & Leader",
        "Journalist", "District Reporter", "State Reporter", 
        "Chief Reporter", "Citizen Reporter", "Digital Reporter", "Mobile Reporter", 
        "News Informer", "Volunteers"
    ];

    const leadershipRoleChoices = [
        "Patron", "Vice Patron", "Honorary President", "President", "Vice President",
        "General Secretary", "Joint Gen. Secretary", "Treasurer", "Ambassador",
        "Organizing Secretary", "Media Secretary", "Political Secretary",
        "Propaganda Secretary", "Legal Secretary",
        "Action Committee Member", "Action Committee Member - 1", "Action Committee Member - 2",
        "Action Committee Member - 3", "Action Committee Member - 4",
        "Action Committee Member - 5", "Action Committee Member - 6",
        "Action Committee Member - 7"
    ];

    const currentRoleChoices = filterType === 'Leader' ? leadershipRoleChoices : memberRoleChoices;

    const increaseFont = () => setFontSize(prev => Math.min(prev + 2, 24));
    const decreaseFont = () => setFontSize(prev => Math.max(prev - 2, 10));

    useEffect(() => {
        fetchMembers();
    }, [filterType]);

    useEffect(() => {
        setFilterRole(preSelectedRole || '');
    }, [preSelectedRole]);

    useEffect(() => {
        setFilterStatus(preSelectedStatus || '');
    }, [preSelectedStatus]);

    useEffect(() => {
        setFilterGender(preSelectedGender || '');
    }, [preSelectedGender]);

    useEffect(() => {
        setFilterWing(preSelectedWing || '');
    }, [preSelectedWing]);

    useEffect(() => {
        setFilterState(preSelectedState || '');
    }, [preSelectedState]);

    useEffect(() => {
        setFilterDistrict(preSelectedDistrict || '');
    }, [preSelectedDistrict]);

    useEffect(() => {
        filterData();
    }, [members, searchQuery, filterState, filterDistrict, filterRole, filterStatus, filterGender, filterMemberType, filterWing, filterYear, filterMonth, sortConfig]);

    const fetchMembers = async () => {
        setLoading(true);
        try {
            const response = await axios.get('nai-members/members/');
            let data = response.data;
            
            // Apply base filter from prop (e.g., 'Leader') BEFORE other filters
            if (filterType) {
                data = data.filter(member => (member.member_type || '').toUpperCase() === filterType.toUpperCase());
            }
            
            setMembers(data);
            setFilteredMembers(data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching members:", error);
            setLoading(false);
        }
    };

    const filterData = () => {
        let temp = [...members];

        // 1. Search Query (Name, ID, Mobile)
        if (searchQuery) {
            const lowerQuery = searchQuery.toLowerCase();
            temp = temp.filter(member => 
                (member.member_name && member.member_name.toLowerCase().includes(lowerQuery)) ||
                (member.member_id && String(member.member_id).padStart(4, '0').includes(lowerQuery)) ||
                (member.mobile && String(member.mobile).includes(lowerQuery))
            );
        }

        // 2. State Filter
        if (filterState) {
            temp = temp.filter(member => (member.state || '').toUpperCase() === filterState.toUpperCase());
        }

        // 3. District Filter (Text match)
        if (filterDistrict) {
            temp = temp.filter(member => member.district && member.district.toLowerCase().includes(filterDistrict.toLowerCase()));
        }

        // 4. Role / Designation Filter
        if (filterRole) {
            const lowerFilterRole = filterRole.toLowerCase();
            temp = temp.filter(member => 
                (member.member_type_choice && member.member_type_choice.toLowerCase() === lowerFilterRole) || 
                (member.leadership_designation && member.leadership_designation.toLowerCase() === lowerFilterRole) ||
                // Handle partial matches for Action Committee if needed, but currently strict match
                (lowerFilterRole.includes("action committee") && member.leadership_designation && member.leadership_designation.toLowerCase().includes(lowerFilterRole))
            );
        }

        // 5. Status Filter
        if (filterStatus) {
            temp = temp.filter(member => {
                const isExpired = member.valid_till && new Date(member.valid_till) < new Date().setHours(0, 0, 0, 0);
                const statusValue = isExpired ? 'EXPIRED' : (member.member_status || '').toUpperCase();
                return statusValue === filterStatus;
            });
        }

        // 5b. Gender Filter
        if (filterGender) {
            temp = temp.filter(member => (member.gender || '').toUpperCase() === filterGender.toUpperCase());
        }

        // 6. Member Type Filter (Only if not fixed by prop)
        if (!filterType && filterMemberType) {
             temp = temp.filter(member => member.member_type === filterMemberType);
        }

        // 7. Wing Filter (Leader only usually, but general search if field exists)
        if (filterWing) {
            temp = temp.filter(member => member.leadership_wing && member.leadership_wing.toLowerCase().includes(filterWing.toLowerCase()));
        }

        // 7b. Year Filter
        if (filterYear) {
            temp = temp.filter(member => member.registered_on && String(new Date(member.registered_on).getFullYear()) === filterYear);
        }

        // 7c. Month Filter
        if (filterMonth) {
            temp = temp.filter(member => {
                if (!member.registered_on) return false;
                const m = (new Date(member.registered_on).getMonth() + 1).toString().padStart(2, '0');
                return m === filterMonth;
            });
        }

        // 8. Sorting
        if (sortConfig.key) {
            temp.sort((a, b) => {
                if (sortConfig.key === 'member_type_choice' && filterType === 'Leader') {
                    const weightA = getRoleWeight(a.leadership_place);
                    const weightB = getRoleWeight(b.leadership_place);
                    
                    if (weightA !== weightB) {
                        return sortConfig.direction === 'asc' ? weightA - weightB : weightB - weightA;
                    }
                    // If same place weight, fall through to alphabetical on designation
                }

                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];
                
                // Special handling to sort by the concatenated Leader string if sorting by Role
                if (sortConfig.key === 'member_type_choice' && filterType === 'Leader') {
                    valA = `${a.leadership_place || ''} ${a.leadership_designation || ''}`.trim();
                    valB = `${b.leadership_place || ''} ${b.leadership_designation || ''}`.trim();
                }

                // Handle different data types or nulls
                if (valA === null || valA === undefined) valA = '';
                if (valB === null || valB === undefined) valB = '';
                if (typeof valA === 'string') valA = valA.toLowerCase();
                if (typeof valB === 'string') valB = valB.toLowerCase();

                if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        setFilteredMembers(temp);
        setCurrentPage(1);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) return <FaSort className="inline ml-1 text-gray-400" />;
        if (sortConfig.direction === 'asc') return <FaSortUp className="inline ml-1 text-blue-500" />;
        return <FaSortDown className="inline ml-1 text-blue-500" />;
    };

    const clearFilters = () => {
        setSearchQuery('');
        setFilterState('');
        setFilterDistrict('');
        setFilterRole('');
        setFilterStatus('');
        setFilterGender('');
        setFilterMemberType('');
        setFilterWing('');
        setFilterYear('');
        setFilterMonth('');
        setCurrentPage(1);
    };

    // Pagination Calculations
    const totalPages = Math.ceil(filteredMembers.length / itemsPerPage) || 1;
    const safeCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
    const indexOfLastItem = safeCurrentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentMembers = filteredMembers.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (newPage) => {
        const targetPage = Math.min(Math.max(newPage, 1), totalPages);
        setCurrentPage(targetPage);
        if (tableTopRef.current) {
            tableTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            let start = Math.max(2, safeCurrentPage - 1);
            let end = Math.min(totalPages - 1, safeCurrentPage + 1);

            if (safeCurrentPage <= 3) {
                start = 2;
                end = 4;
            } else if (safeCurrentPage >= totalPages - 2) {
                start = totalPages - 3;
                end = totalPages - 1;
            }

            if (start > 2) {
                pages.push('...');
            }

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (end < totalPages - 1) {
                pages.push('...');
            }

            pages.push(totalPages);
        }
        return pages;
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this member?")) {
            try {
                await axios.delete(`nai-members/members/${id}/`);
                // Update both lists
                const updatedMembers = members.filter(m => m.id !== id);
                setMembers(updatedMembers);
                // filteredMembers will update via useEffect
            } catch (error) {
                console.error("Error deleting member:", error);
                alert("Failed to delete member.");
            }
        }
    };

    const handleExportCSV = () => {
        try {
            if (!filteredMembers || filteredMembers.length === 0) {
                alert("No members to export matching the current filters.");
                return;
            }

            const headers = [
                "member_name", "member_id", "member_type", "member_designation", "member_status",
                "leader_designation", "leadership_place", "leadership_wing", "registered_on",
                "valid_till", "last_renewed_on", "renewal", "member_age", "membership_age",
                "mobile", "whatsapp_no", "email", "member_address", "state", "district", "city",
                "sub_district", "zone", "taluk", "village", "ward", "pin_code", "police_station",
                "gender", "date_of_birth", "religion", "voter_id", "aadhar_number", "occupation",
                "business", "business_designation", "membership_amount", "membership_payment_date",
                "leadership_amount", "leadership_payment_date", "referrer_name",
                "referrer_mobile_number", "remarks"
            ];
            
            const escapeCSV = (val) => {
                if (val === null || val === undefined) return '""';
                const str = String(val);
                if (str.includes(',') || str.includes('\n') || str.includes('"')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            };

            const csvRows = [];
            csvRows.push(headers.join(","));

            filteredMembers.forEach(member => {
                const regPayment = member.payments?.filter(p => p.payment_type === 'registration').sort((a,b) => new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime())[0];
                const leadPayment = member.payments?.filter(p => p.payment_type === 'leadership').sort((a,b) => new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime())[0];

                const row = [
                    member.member_name,
                    member.member_id,
                    member.member_type,
                    member.member_type_choice,
                    member.member_status,
                    member.leadership_designation,
                    member.leadership_place,
                    member.leadership_wing,
                    formatDateToDDMMYYYY(member.registered_on),
                    formatDateToDDMMYYYY(member.valid_till),
                    formatDateToDDMMYYYY(member.last_renewed_on),
                    member.renewal,
                    member.member_age,
                    member.membership_age,
                    member.mobile,
                    member.whatsapp_no,
                    member.email,
                    member.member_address,
                    member.state,
                    member.district,
                    member.city,
                    member.sub_district,
                    member.zone,
                    member.taluk,
                    member.village,
                    member.ward,
                    member.pin_code,
                    member.police_station,
                    member.gender,
                    formatDateToDDMMYYYY(member.date_of_birth),
                    member.religion,
                    member.voter_id,
                    member.aadhar_number,
                    member.occupation,
                    member.business,
                    member.business_designation,
                    regPayment?.amount || "",
                    formatDateToDDMMYYYY(regPayment?.payment_date) || "",
                    leadPayment?.amount || "",
                    formatDateToDDMMYYYY(leadPayment?.payment_date) || "",
                    member.referrer_name,
                    member.referrer_mobile_number,
                    member.remarks
                ];
                csvRows.push(row.map(escapeCSV).join(","));
            });

            const csvString = "\uFEFF" + csvRows.join("\n"); // Add BOM for Excel UTF-8 support
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filterType ? `${filterType.toLowerCase()}s_export.csv` : 'members_export.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error("Error exporting CSV:", error);
            alert("Failed to export CSV.");
        }
    };

    const handleDownloadSample = async () => {
        try {
            const response = await axios.get('nai-members/members/sample_csv/', {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'member_import_template.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error("Error downloading sample CSV:", error);
            alert("Failed to download sample CSV.");
        }
    };

    const fileInputRef = useRef(null);

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true);
            const response = await axios.post('nai-members/members/import_csv/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 120000 // 2 minutes
            });
            let alertMsg = response.data.message;
            if (response.data.errors && response.data.errors.length > 0) {
                alertMsg += '\n\nRow Errors (First 5):\n' + response.data.errors.slice(0, 5).join('\n');
                if (response.data.errors.length > 5) alertMsg += `\n...and ${response.data.errors.length - 5} more.`;
            }
            alert(alertMsg);
            fetchMembers(); // Refresh list
        } catch (error) {
            console.error("Error importing CSV:", error);
            alert("Failed to import CSV: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
            event.target.value = null; // Reset input
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Loading members...</div>;

    const title = filterType === 'Leader' ? 'Leaders' : 'All Members';

    return (
        <div ref={tableTopRef} className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <h2 className="text-xl font-bold text-gray-800">{title} <span className="text-sm font-normal text-gray-500">({filteredMembers.length})</span></h2>
                <div className="flex flex-wrap gap-2">
                    <button onClick={decreaseFont} className="bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded font-bold" title="Smaller Font">A-</button>
                    <button onClick={increaseFont} className="bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded font-bold" title="Larger Font">A+</button>
                    
                    {/* CSV Buttons */}
                    <button onClick={handleDownloadSample} className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded flex items-center transition-colors shadow-sm text-sm" title="Download Sample CSV">
                        <FaFileDownload className="mr-2" /> Sample
                    </button>
                    <button onClick={handleExportCSV} className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded flex items-center transition-colors shadow-sm text-sm">
                        <FaFileCsv className="mr-2" /> Export
                    </button>
                    <div className="relative">
                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            onChange={handleFileChange} 
                            style={{ display: 'none' }} 
                            accept=".csv"
                        />
                         <button onClick={handleImportClick} className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded flex items-center transition-colors shadow-sm text-sm">
                            <FaUpload className="mr-2" /> Import
                        </button>
                    </div>

                    <button onClick={onAddClick} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors shadow-sm">Add Member</button>
                </div>
            </div>

            {/* Advanced Filters Section */}
            <div className="bg-gray-50 p-4 rounded-md mb-6 border border-gray-200">
                <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Advanced Search</h3>
                    <button onClick={clearFilters} className="text-xs text-red-600 hover:text-red-800 underline">Clear All Filters</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    
                    {/* Search By Name/ID/Mobile */}
                    <div className="col-span-1 md:col-span-1 lg:col-span-2">
                        <input 
                            type="text" 
                            placeholder="Search Name, ID, Mobile..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                        />
                    </div>

                    {/* State Filter */}
                    <div>
                        <select 
                            value={filterState} 
                            onChange={(e) => setFilterState(e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                        >
                            <option value="">All States</option>
                            {actualStates.map(state => <option key={state} value={state}>{state}</option>)}
                        </select>
                    </div>

                    {/* District Filter */}
                    <div>
                         <input 
                            type="text" 
                            placeholder="District..." 
                            value={filterDistrict}
                            onChange={(e) => setFilterDistrict(e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                        />
                    </div>

                     {/* Role Filter */}
                     <div>
                        <select 
                            value={filterRole} 
                            onChange={(e) => setFilterRole(e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                        >
                            <option value="">All Roles</option>
                            {currentRoleChoices.map(role => <option key={role} value={role}>{role}</option>)}
                        </select>
                    </div>

                    {/* Wing Filter (Conditional) with Auto-Suggest */}
                    {filterType === 'Leader' && (
                        <div className="relative">
                            <input 
                                type="text" 
                                placeholder="Search Wing..." 
                                value={filterWing}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setFilterWing(value);
                                    if (value) {
                                        const uniqueWings = [...new Set(members.map(m => m.leadership_wing).filter(w => w))];
                                        const suggestions = uniqueWings.filter(w => w.toLowerCase().includes(value.toLowerCase()));
                                        setWingSuggestions(suggestions);
                                        setShowWingSuggestions(true);
                                    } else {
                                        setShowWingSuggestions(false);
                                    }
                                }}
                                onBlur={() => setTimeout(() => setShowWingSuggestions(false), 200)}
                                onFocus={() => {
                                    if(filterWing) setShowWingSuggestions(true);
                                }}
                                className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                            />
                            {showWingSuggestions && wingSuggestions.length > 0 && (
                                <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-48 overflow-y-auto shadow-lg">
                                    {wingSuggestions.map((wing, index) => (
                                        <li 
                                            key={index}
                                            onClick={() => {
                                                setFilterWing(wing);
                                                setShowWingSuggestions(false);
                                            }}
                                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700"
                                        >
                                            {wing}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {/* Status Filter */}
                    <div>
                        <select 
                            value={filterStatus} 
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                        >
                            <option value="">All Status</option>
                            <option value="ACTIVE">ACTIVE</option>
                            <option value="EXPIRED">EXPIRED</option>
                            <option value="DIED">DIED</option>
                            <option value="DISMISSED">DISMISSED</option>
                            <option value="SUSPENDED">SUSPENDED</option>
                            <option value="UNUSED">UNUSED</option>
                        </select>
                    </div>

                    {/* Year Filter */}
                    <div>
                        <select 
                            value={filterYear} 
                            onChange={(e) => setFilterYear(e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                        >
                            <option value="">All Years</option>
                            {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                        </select>
                    </div>

                    {/* Month Filter */}
                    <div>
                        <select 
                            value={filterMonth} 
                            onChange={(e) => setFilterMonth(e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                        >
                            <option value="">All Months</option>
                            {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </div>

                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" style={{ fontSize: `${fontSize}px` }}>
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('member_id')}>
                                ID {renderSortIcon('member_id')}
                            </th>
                            <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Image</th>
                            <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('member_status')}>
                                Status {renderSortIcon('member_status')}
                            </th>
                            <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('member_name')}>
                                Member Name {renderSortIcon('member_name')}
                            </th>
                            <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('mobile')}>
                                Mobile {renderSortIcon('mobile')}
                            </th>
                            {filterType === 'Leader' ? (
                                <>
                                    <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('district')}>
                                        Location {renderSortIcon('district')}
                                    </th>
                                    <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('member_type_choice')}>
                                        Role {renderSortIcon('member_type_choice')}
                                    </th>
                                    <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Wing</th>
                                </>
                            ) : (
                                <>
                                    <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('district')}>
                                        Location {renderSortIcon('district')}
                                    </th>
                                </>
                            )}
                            {filterType !== 'Leader' && (
                                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Referred By</th>
                            )}
                            <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {currentMembers.map((member) => (
                            <tr key={member.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-lg font-semibold text-gray-700">
                                    {member.member_id ? String(member.member_id).padStart(4, '0') : ''}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {member.picture ? (
                                        <img src={member.picture} alt={member.member_name} className="h-12 w-12 rounded-full object-cover" />
                                    ) : (
                                        <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold text-lg">
                                            {member.member_name ? member.member_name.charAt(0) : '?'}
                                        </div>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {(() => {
                                        const isExpired = member.valid_till && new Date(member.valid_till) < new Date().setHours(0, 0, 0, 0);
                                        const status = isExpired ? 'EXPIRED' : (member.member_status || '').toUpperCase();
                                        return (
                                            <span className={`px-3 py-1 inline-flex text-sm leading-5 font-bold rounded-full ${
                                                status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 
                                                status === 'EXPIRED' ? 'bg-red-100 text-red-800' :
                                                status === 'SUSPENDED' ? 'bg-yellow-100 text-yellow-800' :
                                                status === 'DIED' ? 'bg-orange-100 text-orange-800' :
                                                status === 'DISMISSED' ? 'bg-red-200 text-red-900' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {status}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap font-bold text-gray-900 text-lg">
                                    <button 
                                        onClick={() => onMemberClick && onMemberClick(member.id)}
                                        className="font-bold text-lg text-blue-600 hover:text-blue-800 hover:underline uppercase flex items-center"
                                    >
                                        {member.member_name}
                                        {member.is_association_ambassador && (
                                            <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-bold border border-blue-200" title="Association Ambassador">
                                                AMBASSADOR
                                            </span>
                                        )}
                                    </button>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-base font-semibold text-gray-900">
                                    <div className="flex items-center space-x-3">
                                        <span className="text-lg text-gray-900 font-semibold">{member.mobile}</span>
                                        {member.mobile && (
                                            <div className="flex space-x-2">
                                                <a href={`tel:${member.mobile}`} className="text-blue-500 hover:text-blue-700" title="Call">
                                                    <FaPhone size={16} />
                                                </a>
                                                <a href={`https://wa.me/91${String(member.mobile).replace(/\D/g, '').slice(-10)}`} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-700" title="WhatsApp">
                                                    <FaWhatsapp size={18} />
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </td>
                                {filterType === 'Leader' ? (
                                    <>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-gray-900 text-lg font-semibold">{member.district}</div>
                                            <div className="text-gray-500 text-base">{member.state}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-900 font-semibold uppercase">
                                            {`${member.leadership_place ? member.leadership_place + ' ' : ''}${member.leadership_designation || '-'}`}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-900 font-medium">
                                            {member.leadership_wing || '-'}
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-gray-900 text-lg font-semibold">{member.district}</div>
                                            <div className="text-gray-500 text-base">{member.state}</div>
                                        </td>
                                    </>
                                )}
                                {filterType !== 'Leader' && (
                                    <td className="px-6 py-4 whitespace-nowrap text-lg font-semibold text-gray-900">
                                        {member.referrer_name || '-'}
                                    </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap font-medium">
                                    <button 
                                        onClick={() => onEditClick && onEditClick(member.id)}
                                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                                    >
                                        <FaEdit size={18} />
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(member.id)}
                                        className="text-red-600 hover:text-red-900 mr-4"
                                    >
                                        <FaTrash size={18} />
                                    </button>
                                    <button 
                                        onClick={() => {
                                            const url = member.slug ? `/${member.slug.replace(/^\/+/, '')}` : `/${member.member_id}`;
                                            window.open(url, '_blank');
                                        }}
                                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 rounded inline-flex items-center"
                                        title="Create QR / View Profile"
                                    >
                                        <FaQrcode className="mr-1" /> QR
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredMembers.length === 0 && (
                     <div className="text-center py-10 text-gray-500">No members found matching your filters.</div>
                )}
            </div>

            {/* Pagination Controls */}
            {filteredMembers.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-700">
                        Showing <span className="font-semibold text-gray-900">{filteredMembers.length === 0 ? 0 : indexOfFirstItem + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(indexOfLastItem, filteredMembers.length)}</span> of <span className="font-semibold text-gray-900">{filteredMembers.length}</span> members
                    </div>

                    <div className="flex items-center space-x-1">
                        <button
                            onClick={() => handlePageChange(1)}
                            disabled={safeCurrentPage === 1}
                            className="p-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-600 transition-colors"
                            title="First Page"
                        >
                            <FaAngleDoubleLeft className="w-3.5 h-3.5" />
                        </button>
                        <button
                            onClick={() => handlePageChange(safeCurrentPage - 1)}
                            disabled={safeCurrentPage === 1}
                            className="px-3 py-1.5 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-700 transition-colors flex items-center gap-1.5"
                            title="Previous Page"
                        >
                            <FaChevronLeft className="w-3 h-3" />
                            <span>Prev</span>
                        </button>

                        <div className="flex items-center space-x-1 px-1">
                            {getPageNumbers().map((page, idx) => {
                                if (page === '...') {
                                    return (
                                        <span key={`dots-${idx}`} className="px-2 py-1 text-sm text-gray-400 select-none">
                                            ...
                                        </span>
                                    );
                                }
                                const isActive = safeCurrentPage === page;
                                return (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`min-w-[36px] h-9 px-3 text-sm font-semibold rounded-md border transition-all ${
                                            isActive
                                                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-100 hover:text-gray-900'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => handlePageChange(safeCurrentPage + 1)}
                            disabled={safeCurrentPage === totalPages}
                            className="px-3 py-1.5 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-700 transition-colors flex items-center gap-1.5"
                            title="Next Page"
                        >
                            <span>Next</span>
                            <FaChevronRight className="w-3 h-3" />
                        </button>
                        <button
                            onClick={() => handlePageChange(totalPages)}
                            disabled={safeCurrentPage === totalPages}
                            className="p-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-600 transition-colors"
                            title="Last Page"
                        >
                            <FaAngleDoubleRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MembersList;
